import { useQuery } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssetDetailType } from '@/common/asset';
import type { Pagination } from '@/common/pagination';
import { ROLE } from '@/common/role';
import type { WithDeletedBy } from '@/common/soft-delete';
import type { SubAsset } from '@/common/sub-asset';
import { DeletedBadge, DeletedOnIcon } from '@/components/soft-delete/DeletedInfo';
import { DeletedViewBanner } from '@/components/soft-delete/DeletedViewBanner';
import { RestoreConfirmDialog } from '@/components/soft-delete/RestoreConfirmDialog';
import { DELETED_STATUS, StatusFilterSelect } from '@/components/soft-delete/StatusFilterSelect';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/use-permission';
import { useRestoreItem } from '@/hooks/use-soft-delete';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { getListSubAssetsOptions } from '@/queries/sub-assets/list-sub-assets';
import { buildSubAssetPath, getTreeView } from '../../../lib/utils/asset';
import { AbsoluteLoader } from '../../absoluteLoader';
import { SubAssetCard } from './SubAssetCard';

export type SubAssetsListProps = {
	asset: AssetDetailType | null;
};

export function SubAssetList({ asset }: SubAssetsListProps) {
	const { t } = useTranslation();
	const [pagination, _setPagination] = useState({
		limit: 300,
		offset: 0,
	} as Pagination);
	const query = { assetId: asset?.id || '' };

	const {
		data: subAssetsResponse,
		isLoading,
		refetch,
		isRefetching,
	} = useQuery(getListSubAssetsOptions(asset?.id as string, { ...query, ...pagination }));

	const subAssetsWithFirstParent = useMemo((): SubAsset[] => {
		// If no sub-assets, return empty array
		if (!subAssetsResponse?.subAssets) return [];

		// Use getTreeView to structure sub-assets hierarchically
		const children = getTreeView(subAssetsResponse?.subAssets);
		return [
			{
				id: asset?.id as string,
				name: asset?.name || '',
				parentSubAssetId: null,
				children,
			},
		];
	}, [subAssetsResponse?.subAssets, asset?.id, asset?.name]);

	const onChangeInList = () => {
		refetch();
	};

	// Status "Deleted" (GH #331): a flat list of the sub-assets deleted on their own (their parent is live),
	// each with its path in the live tree; children deleted with them come back with them
	const canDelete = usePermission(ROLE.SUB_ASSETS_DELETE);
	const [showDeleted, setShowDeleted] = useState(false);
	const deleted = canDelete && showDeleted;
	const { data: deletedResponse, isLoading: isDeletedLoading } = useQuery(
		getListSubAssetsOptions(asset?.id as string, { ...query, ...pagination, deleted: true }, deleted),
	);
	const deletedSubAssets = (deletedResponse?.subAssets ?? []) as WithDeletedBy<SubAsset>[];
	const [subAssetToRestore, setSubAssetToRestore] = useState<SubAsset | null>(null);
	const restoreSubAsset = useRestoreItem({
		onRestored: () => {
			setSubAssetToRestore(null);
			refetch();
		},
	});
	const subAssetName = (subAsset: SubAsset) => subAsset.name || t('common.entities.subAsset', { count: 1 });
	// Where it hung: the asset, then the live parents ("Bridge A › Span 2")
	const pathOf = (subAsset: SubAsset) =>
		[
			asset?.name,
			...(subAsset.parentSubAssetId
				? buildSubAssetPath(subAssetsWithFirstParent, subAsset.parentSubAssetId)
						.slice(1)
						.map((node) => node.name)
				: []),
		]
			.filter(Boolean)
			.join(' › ');

	return (
		<div className="flex-1 h-full overflow-auto">
			<div className="space-y-4">
				<div className="flex flex-wrap justify-between items-center gap-2 mb-4">
					<h3 className="font-medium">{t('assets.structure.title')}</h3>
					{canDelete && (
						<StatusFilterSelect
							value={showDeleted ? DELETED_STATUS : 'active'}
							onValueChange={(value) => setShowDeleted(value === DELETED_STATUS)}
							options={[{ value: 'active', label: t('common.states.active') }]}
							canShowDeleted
							ariaLabel={t('common.deleted.statusFilter')}
						/>
					)}
				</div>
				{deleted ? (
					<div className="space-y-3">
						<DeletedViewBanner />
						<div className="relative rounded-lg border bg-card text-card-foreground shadow-sm divide-y">
							<AbsoluteLoader className="rounded-lg" isLoading={isDeletedLoading} />
							{deletedSubAssets.map((subAsset) => (
								<div key={subAsset.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
									<div className="min-w-0">
										<p className="font-medium flex items-center gap-2 min-w-0">
											<span className="truncate">{subAsset.name}</span>
											<DeletedBadge />
											<DeletedOnIcon deletedAt={subAsset.deletedAt} deletedBy={subAsset.deletedBy} />
										</p>
										<p className="text-xs text-muted-foreground truncate">{pathOf(subAsset)}</p>
									</div>
									<Button variant="outline" size="sm" onClick={() => setSubAssetToRestore(subAsset)}>
										<RotateCcw />
										{t('common.actions.restore')}
									</Button>
								</div>
							))}
							{!isDeletedLoading && deletedSubAssets.length === 0 && (
								<p className="p-6 text-center text-sm text-muted-foreground">{t('common.deleted.empty')}</p>
							)}
						</div>
						{subAssetToRestore?.id && (
							<RestoreConfirmDialog
								open
								onOpenChange={(open) => !open && setSubAssetToRestore(null)}
								target={softDeleteTargets.subAsset(subAssetToRestore.id)}
								name={subAssetName(subAssetToRestore)}
								isRestoring={restoreSubAsset.isPending}
								onConfirm={() =>
									restoreSubAsset.mutate({
										target: softDeleteTargets.subAsset(subAssetToRestore.id as string),
										name: subAssetName(subAssetToRestore),
									})
								}
							/>
						)}
					</div>
				) : (
					<div className="relative h-full">
						{<AbsoluteLoader className="rounded-lg" isLoading={isLoading || isRefetching} />}
						<SubAssetCard
							subAssets={subAssetsWithFirstParent}
							assetId={asset?.id as string}
							onChangeInList={onChangeInList}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
